import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { fmtDate, titleCase } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS = ["active", "all", "open", "in_progress", "waiting_partner", "waiting_customer", "resolved", "closed"];
const PRIORITY = ["all", "low", "medium", "high", "urgent"];

const STATUS_LABEL = {
  active: "Active (excludes resolved/closed)",
  all: "All cases",
  open: "Open",
  in_progress: "In progress",
  waiting_partner: "Waiting on partner",
  waiting_customer: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
};

export default function SupportCases() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [priority, setPriority] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { page, per_page: 25 };
    if (search) params.search = search;
    if (priority !== "all") params.priority = priority;
    if (status === "active") {
      params.exclude_resolved = true;
    } else if (status !== "all") {
      params.status = status;
    }
    api.get("/support-cases", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, status, priority, page]);

  const columns = [
    { key: "case_id", header: "Case", render: (r) => <span className="font-mono text-xs" data-testid={`case-id-${r.id}`}>{r.case_id}</span> },
    { key: "subject", header: "Subject", render: (r) => (
      <div className="max-w-[300px] truncate"><div className="text-sm">{r.subject}</div><div className="text-xs text-muted-foreground">{r.customer_name} · {r.supplier_name}</div></div>
    )},
    { key: "type", header: "Type", render: (r) => <span className="text-xs">{titleCase(r.type)}</span> },
    { key: "priority", header: "Priority", render: (r) => <StatusBadge status={r.priority} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "assigned_agent_name", header: "Assigned", render: (r) => <span className="text-xs">{r.assigned_agent_name || "—"}</span> },
    { key: "created_at", header: "Created", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span> },
  ];

  return (
    <div data-testid="support-cases-page">
      <PageHeader title="Support Cases" description="Partner and customer support tickets from the live .NET backend." />
      <div className="pt-6">
        <DataTable
          columns={columns}
          rows={data.items}
          total={data.total}
          page={page}
          pageSize={25}
          loading={loading}
          onPageChange={setPage}
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Search case ID, subject, booking…"
          toolbar={
            <>
              <Select value={priority} onValueChange={(v) => { setPriority(v); setPage(1); }}>
                <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="cases-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>{PRIORITY.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All priorities" : titleCase(s)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger className="w-[220px] h-9 text-sm" data-testid="cases-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </>
          }
          onRowClick={(r) => nav(`/support/${r.id}`)}
          emptyTitle="No support cases"
          testIdPrefix="cases"
        />
      </div>
    </div>
  );
}
